import { Box } from "@mui/material";
import { isMentionAll, type ChatMention } from "@xop/dashboard-shared";
import type { JSX, MouseEvent } from "react";

/**
 * The @-mention dropdown shown above the composer input. Keyboard nav lives in
 * the composer (arrow/enter on the input); this renders the ranked matches and
 * reports hover + click. Extracted from `ChatComposer.tsx`.
 *
 * `@channel` (the notify-everyone sentinel, room-only) carries a hint so it
 * doesn't read as a teammate who happens to be named "channel".
 */
export function MentionPicker({
  matches,
  activeIdx,
  onPick,
  onHover,
}: {
  matches: ChatMention[];
  activeIdx: number;
  onPick: (c: ChatMention) => void;
  onHover: (i: number) => void;
}): JSX.Element {
  return (
    <Box
      data-testid="chat-mention-picker"
      sx={{
        position: "absolute",
        bottom: "100%",
        left: 12,
        right: 12,
        mb: 0.5,
        zIndex: 5,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        boxShadow: 3,
        maxHeight: 220,
        overflowY: "auto",
      }}
    >
      {matches.map((c, i) => (
        <Box
          key={c.user_id}
          component="button"
          type="button"
          data-testid="chat-mention-option"
          onMouseDown={(e: MouseEvent<HTMLButtonElement>) => e.preventDefault()}
          onClick={() => onPick(c)}
          onMouseEnter={() => onHover(i)}
          sx={{
            display: "block",
            width: "100%",
            textAlign: "left",
            px: 1.5,
            py: 1,
            border: 0,
            cursor: "pointer",
            fontSize: 14,
            fontFamily: "inherit",
            color: "text.primary",
            bgcolor: i === activeIdx ? "action.hover" : "transparent",
          }}
        >
          @{c.display_name}
          {isMentionAll(c) && (
            <Box
              component="span"
              data-testid="chat-mention-all-hint"
              sx={{ ml: 1, color: "text.secondary", fontSize: 12 }}
            >
              Notify everyone on the team
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
